import { useEffect, useRef } from "react";
import {
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import type { Project, Session, User } from "../../api/types.js";
import { demoAssets } from "../shared/constants.js";
import type { WorkspaceView } from "../workspace/workspaceView.js";
import { ProjectList } from "./ProjectList.js";
import { SidebarNav } from "./SidebarNav.js";
import { SessionList } from "./SessionList.js";
import { UserProfileCard } from "./UserProfileCard.js";
import { PROJECT_SUBMENU_HEIGHT, PROJECT_SUBMENU_WIDTH, SESSION_MENU_HEIGHT, SESSION_MENU_WIDTH, useFloatingMenu } from "./useFloatingMenu.js";

export function Sidebar({
  user,
  projects,
  sessions,
  currentProjectId,
  currentSession,
  activeRunSessionId = null,
  activeView,
  collapsed,
  onToggleCollapsed,
  onNewSession,
  onOpenSearch,
  onOpenSkills,
  onOpenSession,
  onRenameSession,
  onMoveSession,
  onTogglePinSession,
  onDeleteSession,
  onRenameProject,
  onDeleteProject,
  onAddProject,
  onProjectChange,
  onLogout
}: {
  user: User;
  projects: Project[];
  sessions: Session[];
  currentProjectId: string;
  currentSession: Session | null;
  activeRunSessionId?: string | null;
  activeView: WorkspaceView;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onNewSession: () => void;
  onOpenSearch: () => void;
  onOpenSkills: () => void;
  onOpenSession: (session: Session) => void;
  onRenameSession: (session: Session) => void;
  onMoveSession: (session: Session, projectId: string | null) => void;
  onTogglePinSession: (session: Session) => void;
  onDeleteSession: (session: Session) => void;
  onRenameProject: (project: Project) => void;
  onDeleteProject: (project: Project) => void;
  onAddProject: () => void;
  onProjectChange: (projectId: string) => void;
  onLogout: () => void;
}) {
  const activeProject = projects.find((project) => project.id === currentProjectId);
  const sessionMenu = useFloatingMenu();
  const projectSubmenu = useFloatingMenu();
  const projectMenu = useFloatingMenu();
  const sidebarScrollRef = useRef<HTMLDivElement>(null);
  const sidebarScrollActiveTimerRef = useRef<number | null>(null);

  function closeSessionMenu() {
    sessionMenu.close();
    projectSubmenu.close();
  }

  function closeProjectMenu() {
    projectMenu.close();
  }

  function closeFloatingMenus() {
    closeSessionMenu();
    closeProjectMenu();
  }

  useEffect(() => {
    if (!sessionMenu.openId && !projectMenu.openId) return;
    const closeOnPointer = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (
        !target?.closest("[data-session-menu-root]")
        && !target?.closest("[data-project-menu-root]")
        && !target?.closest("[data-session-menu-layer]")
      ) {
        closeFloatingMenus();
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeFloatingMenus();
    };
    const closeOnResize = () => closeFloatingMenus();
    document.addEventListener("pointerdown", closeOnPointer);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", closeOnResize);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointer);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [sessionMenu.openId, projectMenu.openId]);

  useEffect(() => {
    if (sessionMenu.openId && !sessions.some((session) => session.id === sessionMenu.openId)) {
      closeSessionMenu();
    }
  }, [sessionMenu.openId, sessions]);

  useEffect(() => {
    if (projectMenu.openId && !projects.some((project) => project.id === projectMenu.openId)) {
      closeProjectMenu();
    }
  }, [projectMenu.openId, projects]);

  useEffect(() => {
    const scroller = sidebarScrollRef.current;
    if (!scroller) return;
    const showScrollbar = () => {
      scroller.classList.add("is-scroll-active");
      if (sidebarScrollActiveTimerRef.current) window.clearTimeout(sidebarScrollActiveTimerRef.current);
      sidebarScrollActiveTimerRef.current = window.setTimeout(() => {
        scroller.classList.remove("is-scroll-active");
        sidebarScrollActiveTimerRef.current = null;
      }, 850);
    };
    scroller.addEventListener("scroll", showScrollbar, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", showScrollbar);
      if (sidebarScrollActiveTimerRef.current) window.clearTimeout(sidebarScrollActiveTimerRef.current);
      sidebarScrollActiveTimerRef.current = null;
      scroller.classList.remove("is-scroll-active");
    };
  }, []);

  return (
    <aside className="sidebar" aria-label="主导航">
      <div className="brand-row">
        <button className="brand-toggle" type="button" onClick={onToggleCollapsed} aria-label={collapsed ? "展开侧边栏" : "收起侧边栏"}>
          <span className="brand-mark"><img src={demoAssets.mark} alt="" /></span>
          {collapsed ? <ChevronsRight className="sidebar-open-icon" size={18} /> : null}
        </button>
        <strong>Truth Seeker</strong>
        <button className="icon-button sidebar-collapse" type="button" onClick={onToggleCollapsed} aria-label={collapsed ? "展开侧边栏" : "收起侧边栏"}>
          {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
        </button>
      </div>

      <SidebarNav
        collapsed={collapsed}
        activeView={activeView}
        currentSession={currentSession}
        sessions={sessions}
        onNewSession={onNewSession}
        onOpenSearch={onOpenSearch}
        onOpenSkills={onOpenSkills}
        onOpenSession={onOpenSession}
      />

      <div className="sidebar-scroll" ref={sidebarScrollRef}>
        <ProjectList
          projects={projects}
          currentProjectId={currentProjectId}
          openProjectMenuId={projectMenu.openId}
          projectMenuPosition={projectMenu.position}
          openProjectMenu={projectMenu.open}
          closeProjectMenu={closeProjectMenu}
          closeSessionMenu={closeSessionMenu}
          closeFloatingMenus={closeFloatingMenus}
          onAddProject={onAddProject}
          onProjectChange={onProjectChange}
          onRenameProject={onRenameProject}
          onDeleteProject={onDeleteProject}
        />

        <SessionList
          sessions={sessions}
          projects={projects}
          currentSession={currentSession}
          activeRunSessionId={activeRunSessionId}
          openSessionMenuId={sessionMenu.openId}
          sessionMenuPosition={sessionMenu.position}
          openProjectSubmenuId={projectSubmenu.openId}
          projectSubmenuPosition={projectSubmenu.position}
          openSessionMenu={(sessionId, rect) => sessionMenu.open(sessionId, rect, SESSION_MENU_WIDTH, SESSION_MENU_HEIGHT)}
          closeSessionMenu={closeSessionMenu}
          closeProjectMenu={closeProjectMenu}
          openProjectSubmenu={(sessionId, rect) => projectSubmenu.open(sessionId, rect, PROJECT_SUBMENU_WIDTH, PROJECT_SUBMENU_HEIGHT)}
          closeProjectSubmenu={projectSubmenu.close}
          onOpenSession={onOpenSession}
          onRenameSession={onRenameSession}
          onMoveSession={onMoveSession}
          onTogglePinSession={onTogglePinSession}
          onDeleteSession={onDeleteSession}
        />
      </div>

      <UserProfileCard user={user} activeProject={activeProject} onLogout={onLogout} />
    </aside>
  );
}
